/**
 * Commercial model help: select with one-line meanings, a comparison drawer, and an inline
 * "how this works in the application" panel for the selected model.
 */
import React, { useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Select, Space, Tag, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  COMMERCIAL_MODELS,
  commercialModelInfo,
  type CommercialModelInfo,
  type RecommendedContent,
} from '@/constants/epcCommercialModels';

const { Text, Paragraph } = Typography;

const CONTENT_LABEL: Record<RecommendedContent, string> = {
  MILESTONES: 'Payment milestones',
  SOV: 'Schedule of Values',
  SOV_AND_MILESTONES: 'Milestones + Schedule of Values',
};

export function recommendedContentLabel(info: CommercialModelInfo): string {
  return CONTENT_LABEL[info.recommendedContent];
}

type SelectProps = {
  value?: string;
  onChange?: (v: string) => void;
  disabled?: boolean;
  allowClear?: boolean;
};

/** Form-compatible select; legacy codes (e.g. LumpSum) display as their label. */
export const CommercialModelSelect: React.FC<SelectProps> = ({ value, onChange, disabled, allowClear }) => (
  <Select
    value={value ? commercialModelInfo(value)?.label ?? value : undefined}
    onChange={onChange}
    disabled={disabled}
    allowClear={allowClear}
    showSearch
    placeholder="Select model"
    optionLabelProp="label"
    optionFilterProp="label"
    options={COMMERCIAL_MODELS.map((m) => ({
      value: m.label,
      label: m.label,
      title: m.summary,
    }))}
    optionRender={(opt) => (
      <div style={{ whiteSpace: 'normal' }}>
        <div>{String(opt.label)}</div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {String((opt.data as { title?: string }).title ?? '')}
        </Text>
      </div>
    )}
  />
);

function ModelDetails({ info }: { info: CommercialModelInfo }) {
  return (
    <Descriptions size="small" column={1} bordered labelStyle={{ width: 190 }}>
      <Descriptions.Item label="What it means">{info.meaning}</Descriptions.Item>
      <Descriptions.Item label="Contract value">
        {info.contractValueMeaning}
        {info.valueIsEstimate ? (
          <Tag color="orange" style={{ marginLeft: 8 }}>
            Estimate
          </Tag>
        ) : null}
      </Descriptions.Item>
      <Descriptions.Item label="What to enter">
        <Tag color="blue">{recommendedContentLabel(info)}</Tag>
        <div style={{ marginTop: 4 }}>{info.whatToEnter}</div>
      </Descriptions.Item>
      <Descriptions.Item label="How IPCs are valued">{info.ipcBasis}</Descriptions.Item>
      <Descriptions.Item label="Controls in the application">
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {info.keyControls.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </Descriptions.Item>
    </Descriptions>
  );
}

/** "What does this mean?" link opening a comparison of all models. */
export const CommercialModelGuideLink: React.FC<{ current?: string | null }> = ({ current }) => {
  const [open, setOpen] = useState(false);
  const currentKey = commercialModelInfo(current)?.key;
  return (
    <>
      <Button type="link" size="small" icon={<QuestionCircleOutlined />} style={{ padding: 0 }} onClick={() => setOpen(true)}>
        What does this mean?
      </Button>
      <Drawer title="Commercial models" width={760} open={open} onClose={() => setOpen(false)} destroyOnClose>
        <Paragraph type="secondary">
          The commercial model sets how the contract price behaves and how each Interim Payment Application (IPC) is
          valued. It decides what you should enter on the contract and which over-claims the application warns about.
          Warnings never block saving.
        </Paragraph>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {COMMERCIAL_MODELS.map((m) => (
            <div key={m.key}>
              <Space style={{ marginBottom: 6 }}>
                <Text strong style={{ fontSize: 15 }}>
                  {m.label}
                </Text>
                {m.key === currentKey ? <Tag color="green">This contract</Tag> : null}
              </Space>
              <Paragraph style={{ marginBottom: 6 }}>{m.summary}</Paragraph>
              <ModelDetails info={m} />
            </div>
          ))}
        </Space>
      </Drawer>
    </>
  );
};

/** Inline explanation of the selected model. */
export const CommercialModelExplainer: React.FC<{ model?: string | null; compact?: boolean }> = ({ model, compact }) => {
  const info = commercialModelInfo(model);
  if (!model) return null;
  if (!info) {
    return (
      <Alert
        type="warning"
        showIcon
        message={`"${model}" is not a standard commercial model`}
        description={
          <span>
            Choose one of the standard models so the application can guide content and IPC checks.{' '}
            <CommercialModelGuideLink />
          </span>
        }
      />
    );
  }
  return (
    <Alert
      type="info"
      showIcon
      message={
        <Space wrap>
          <span>How {info.label} works in the application</span>
          <CommercialModelGuideLink current={info.label} />
        </Space>
      }
      description={
        compact ? (
          <div>
            <div>{info.summary}</div>
            <div>
              <Text strong>Enter: </Text>
              {recommendedContentLabel(info)}. <Text strong>IPC: </Text>
              {info.ipcBasis}
            </div>
          </div>
        ) : (
          <ModelDetails info={info} />
        )
      }
    />
  );
};
